import React from 'react';
import { createPortal } from 'react-dom';

const PlayerFilters = ({
  activeFilters,
  setActiveFilters,
  isFilterActive,
  toggleFilter,
  clearAllFilters,
  selectedWithoutPlayers,
  setSelectedWithoutPlayers,
  teamPlayers,
  showWithoutSelect,
  setShowWithoutSelect,
  updateDropdownPosition,
  dropdownPosition,
  dvpRanks,
  player,
  selectedStat,
  showStatFilterInput,
  setShowStatFilterInput,
  statFilterStat,
  setStatFilterStat,
  statFilterOperator,
  setStatFilterOperator,
  statFilterValue,
  setStatFilterValue,
  tempStatFilter,
  setTempStatFilter,
  statFilters,
  setStatFilters,
  playerName,
  players,
  fixtures,
  selectedMatchId,
  selectedSeasons
}) => {
  return (
    <div className="mt-0.5">
      <div className="flex flex-col gap-2">
        {/* Basic Filters Group */}
        <div className="flex items-start lg:items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-[40px] flex-shrink-0 mt-1.5 lg:mt-0">Basic:</span>
          <div className="flex flex-wrap items-center gap-1">
            <button
              onClick={clearAllFilters}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                activeFilters.length === 0 && !selectedWithoutPlayers.length && statFilters.length === 0
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Games
            </button>
            {['home', 'away', 'wins', 'losses'].map(filterType => (
              <button
                key={filterType}
                onClick={() => toggleFilter(filterType)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  isFilterActive(filterType) 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Match Context Group */}
        <div className="flex items-start lg:items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-[40px] flex-shrink-0 mt-1.5 lg:mt-0">Context:</span>
          <div className="flex flex-wrap items-center gap-1">
            {/* Venue dropdown */}
            <div className="relative inline-block">
              <button
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  activeFilters.some(f => f.type === 'venue')
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {activeFilters.find(f => f.type === 'venue')?.value || 'Venue'}
              </button>
              <select
                value={activeFilters.find(f => f.type === 'venue')?.value || ''}
                onChange={(e) => {
                  // Remove any existing venue filter
                  setActiveFilters(prev => prev.filter(f => f.type !== 'venue'));
                  // Add new venue filter if a value is selected
                  if (e.target.value) {
                    toggleFilter('venue', e.target.value);
                  }
                }}
                className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer"
              >
                <option value="">All Venues</option>
                {[...new Set(players?.map(p => p.venue))]
                  .filter(Boolean)
                  .sort()
                  .map((venue, index) => (
                    <option key={index} value={venue}>{venue}</option>
                  ))
                }
              </select>
            </div>

            {/* Opposition dropdown */}
            <div className="relative inline-block">
              <button
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  activeFilters.some(f => f.type === 'opposition')
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {activeFilters.find(f => f.type === 'opposition')?.value 
                  ? `vs ${activeFilters.find(f => f.type === 'opposition').value}`
                  : 'Opposition'}
              </button>
              <select
                value={activeFilters.find(f => f.type === 'opposition')?.value || ''}
                onChange={(e) => {
                  // Remove any existing opposition filter
                  setActiveFilters(prev => prev.filter(f => f.type !== 'opposition'));
                  // Add new opposition filter if a value is selected
                  if (e.target.value) {
                    toggleFilter('opposition', e.target.value);
                  }
                }}
                className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer"
              >
                <option value="">All Teams</option>
                {(() => {
                  if (!players || !playerName) return null;
                  
                  // Get all unique opponents
                  const opponents = [...new Set(players
                    .filter(p => p.player_name === playerName)
                    .map(game => game.is_home_game ? game.away_team : game.home_team)
                    .filter(Boolean)
                  )].sort();
                  
                  return opponents.map((opponent, index) => (
                    <option key={index} value={opponent}>{opponent}</option>
                  ));
                })()}
              </select>
            </div>

            {/* Weather conditions dropdown */}
            {(() => {
              if (!players || !playerName) return null;
              
              const playerGames = players.filter(p => p.player_name === playerName && p.date);
              if (playerGames.length === 0) return null;
              
              const weatherConditions = [...new Set(playerGames
                .filter(game => game.match_weather_type)
                .map(game => game.match_weather_type))];
              
              if (weatherConditions.length === 0) return null;

              // Find selected weather filter
              const selectedWeathers = activeFilters.filter(f => f.type === 'weather').map(f => f.value);

              return (
                <div className="relative inline-flex items-center gap-1">
                  {selectedWeathers.length > 0 ? (
                    selectedWeathers.map(weather => (
                      <div key={weather} className="relative inline-flex items-center">
                        <button
                          className="px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white"
                        >
                          {weather}
                        </button>
                        <button
                          onClick={() => toggleFilter('weather', weather)}
                          className="ml-1 text-gray-400 hover:text-gray-600"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="relative inline-block">
                      <button
                        className="px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        Weather
                      </button>
                      <select
                        value=""
                        onChange={(e) => toggleFilter('weather', e.target.value)}
                        className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer"
                      >
                        <option value="">Select weather...</option>
                        {weatherConditions.map((weather, index) => (
                          <option key={index} value={weather}>{weather}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PlayerFilters; 